import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DataTable from "../../components/common/DataTable";
import { Button, Card } from "react-bootstrap";
import { formatToUSDate } from "../../utils/dateFormat";
import { useDispatch, useSelector } from "react-redux";
import MemberModal from "../../components/member/MemberModal";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { SET_LOADING } from "../../redux/user";
import giftIcon from "../../assets/images/previewmembership/gift.svg";

function RegisterMembership() {
  const [members, setMembers] = useState([]);
  const [tempFamilyMembers, setTempFamilyMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [plan, setPlan] = useState([]);
  const [relationships, setRelationships] = useState([]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.USER_DATA);
  const location = useLocation();
  const pendingMember = location.state?.pending_member;

  const calculateIsChild = (dob) => {
    if (!dob) return false;
    const ageDifMs = Date.now() - new Date(dob).getTime();
    const ageDate = new Date(ageDifMs);
    const age = Math.abs(ageDate.getUTCFullYear() - 1970);
    return age < 5;
  };

  const getRelationships = async () => {
    try {
      const res = await API.get(
        api_paths.member.family_member.GET_RELATIONSHIP
      );
      return res.data?.payload?.result || [];
    } catch (err) {
      return [];
    }
  };

  const calculateMembershipAmount = () => {
    if (!plan)
      return {
        primaryAmount: 0,
        familyAmount: 0,
        totalDiscount: 0,
        totalAmount: 0,
      };

    const amount = plan.amount || 0;
    const discount = plan.discount_amount || 0;

    const discountTillDate = plan.discount_till_date || null;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    // discount applies ONLY when discount_amount is present AND discount_till_date is provided and still valid (inclusive)
    const discountValid =
      !!discount &&
      !!discountTillDate &&
      new Date(discountTillDate).setHours(0, 0, 0, 0) >= todayStart.getTime();

    const effectiveDiscount = discountValid ? discount : 0;

    const isPending = !!pendingMember;
    const primaryIncluded = isPending || !user?.is_membership_active;

    const primaryAmount = primaryIncluded ? amount : 0;

    const currentFamilyMembers = isPending ? tempFamilyMembers : members;
    const payableFamilyMembers = currentFamilyMembers?.filter((m) => {
      const isChild = m.is_child ?? calculateIsChild(m.date_of_birth);
      return !isChild;
    });

    const familyCount = payableFamilyMembers?.length || 0;
    const familyAmount = familyCount * amount;

    const totalDiscount =
      (primaryIncluded ? effectiveDiscount : 0) +
      familyCount * effectiveDiscount;
    const totalAmount = primaryAmount + familyAmount - totalDiscount;

    return {
      primaryAmount,
      familyAmount,
      totalDiscount,
      totalAmount,
      discountValid,
      discountAmount: discount,
      discountTillDate,
      primaryIncluded,
    };
  };

  const {
    primaryAmount,
    familyAmount,
    totalDiscount,
    totalAmount,
    discountValid,
    discountAmount,
    // discountTillDate,
  } = calculateMembershipAmount();

  const displayUser = pendingMember || user;

  const customPrimaryRow = {
    _id: "primary-custom-row",
    first_name: `${displayUser?.first_name || ""} ${
      displayUser?.middle_name ? displayUser.middle_name + " " : ""
    }${displayUser?.last_name || ""}`,
    date_of_birth: formatToUSDate(displayUser?.date_of_birth),
    gender: displayUser?.gender ? displayUser.gender.toLowerCase() : "",
    marital_status: displayUser?.marital_status || "",
    relationship: "Primary",
  };

  const fetchMembers = async () => {
    if (pendingMember) return; // Skip if in pending mode
    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(api_paths.member.family_member.GET_MEMBER);

      if (response.status === 200) {
        setMembers(response?.data?.payload?.result || []);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  const handleSaveLocal = async (data) => {
    const formattedMember = {
      ...data,
      _id: editData?._id || `temp-${Date.now()}`,
    };

    if (editData) {
      setTempFamilyMembers((prev) =>
        prev.map((m) => (m._id === editData._id ? formattedMember : m))
      );
    } else {
      setTempFamilyMembers((prev) => [...prev, formattedMember]);
    }
    setEditData(null);
  };

  const fetchPlanAmount = async () => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(api_paths.member.membership.GET_PLAN);

      if (response.status === 200) {
        const amount = response.data?.payload?.result || 0;

        if (Array.isArray(amount) && amount.length > 0) {
          setPlan(amount[0]); // store first plan object
        }
      }
    } catch (error) {
      console.error("Error fetching plan amount:", error);
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  const handleProcessToPay = async () => {
    try {
      if (!plan?._id) {
        alert("Plan is not available.");
        return;
      }
      dispatch(SET_LOADING(true));

      const { totalAmount, primaryIncluded } = calculateMembershipAmount();

      const payload = {
        family_members: !pendingMember
          ? members.filter((m) => !m.is_child).map((m) => m._id)
          : [],
        pending_family_members: pendingMember
          ? tempFamilyMembers.map(({ _id, ...rest }) => rest)
          : [],
        payment_type: "membership",
        plan_id: plan._id,
        amount: totalAmount,
        primary_included: primaryIncluded,
        pending_member_id: pendingMember?._id,
      };

      const response = await API.post(
        `${api_paths.member.payment.PROCESS_TO_PAY_MEMBERSHIP}?type=register`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data?.payload?.token;
        // const encodedToken = encodeURIComponent(rawToken);

        // Create a POST form dynamically
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;
        // form.target = "_blank";

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);

        form.submit(); // open HPP in new tab
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert("Failed to initiate payment.");
    }
  };

  const membershipColumns = [
    {
      key: "first_name",
      label: "Name",
      render: (_, row) =>
        `${row.first_name || ""} ${
          row.middle_name ? row.middle_name + " " : ""
        }${row.last_name || ""}`,
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      render: (val) => formatToUSDate(val),
    },
    {
      key: "gender",
      label: "Gender",
      render: (val) => <span className="text-capitalize">{val}</span>,
    },
    {
      key: "marital_status",
      label: "Marital Status",
      render: (val) => <span className="text-capitalize">{val}</span>,
    },
    {
      key: "relationship",
      label: "Relationship",
      render: (_, row) => {
        if (row.relationship === "Primary") return "Primary";
        const rel = relationships.find((r) => r._id === row.relationship_id);
        return rel?.name || "-";
      },
    },
  ];

  const handleDelete = async (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };
  const handleDeleteConfirmed = async (row) => {
    if (row.relationship === "Primary") return;

    if (pendingMember) {
      setTempFamilyMembers((prev) => prev.filter((m) => m._id !== row._id));
      return;
    }

    try {
      dispatch(SET_LOADING(true));
      await API.delete(
        `${api_paths.member.family_member.DELETE_MEMBER}/${row._id}`
      );

      const response = await API.get(api_paths.member.family_member.GET_MEMBER);

      setMembers(response.data.payload.result || []);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete member. Try again.");
    } finally {
      dispatch(SET_LOADING(false));
    }
  };
  useEffect(() => {
    const loadData = async () => {
      const rels = await getRelationships();
      setRelationships(rels);

      await Promise.all([fetchMembers(), fetchPlanAmount()]);

      dispatch(SET_LOADING(false));
    };

    loadData();
    // eslint-disable-next-line
  }, []);

  return (
    <div>
      <div className="container pb-5 pt-4">
        <div className="row d-flex mb-1 mx-auto">
          <div className="col-lg-8  d-flex align-items-center px-0 text-center text-lg-start my-3 my-lg-0">
            <h1 className="mb-0 fw-bold text-main f-20">
              Manage Your Membership & Family Members
            </h1>
          </div>
          <div className="col-lg-4 d-flex justify-content-center justify-content-lg-end px-0">
            <button
              className="btn btn-orange-md"
              onClick={() => {
                setEditData(null);
                setShowModal(true);
              }}
            >
              Add Member
            </button>
            {!pendingMember && (
              <button
                type="submit"
                className="btn btn-black px-4 ms-4"
                onClick={() => navigate("/dashboard")}
              >
                Dashboard
              </button>
            )}
          </div>
        </div>
        <div className="row">
          <div className="h1 mb-4 f-16 danger-text fw-semibold">
            Note: Payment required. Registration must be completed again if you
            cancelled process.
          </div>
          <div className="col-lg-8 mb-4 mb-lg-0">
            <DataTable
              columns={membershipColumns}
              data={[
                customPrimaryRow,
                ...(pendingMember ? tempFamilyMembers : members),
              ]}
              loading={loading}
              onEdit={(row) => {
                if (row.relationship !== "Primary") {
                  setEditData(row);
                  setShowModal(true);
                }
              }}
              onDelete={handleDelete}
            />
          </div>
          <div className="col-lg-4">
            <Card className="fees-card">
              <div className="d-flex fees-card-header px-4 py-3">
                <h5 className="fw-semibold fs-16 text-main mb-0">
                  Membership Fees
                </h5>
                <img src={giftIcon} alt="icon" className="ms-auto" />
              </div>
              <div className="px-4 py-3">
                {/* Annual Plan Name */}
                <div className="mt-1 plan-title">
                  <strong>{plan?.plan_name}</strong>
                </div>

                {/* Primary Member */}
                <div className="d-flex justify-content-between mt-3 fees-content-1">
                  <div>
                    <div>Primary Member 1 × ${plan?.amount}</div>
                    {plan?.discount_amount && discountValid && (
                      <div className="small">
                        (${discountAmount} discount applied)
                      </div>
                    )}
                  </div>

                  <strong>${primaryAmount}</strong>
                </div>

                {/* Family Members */}
                {(pendingMember ? tempFamilyMembers : members).length > 0 && (
                  <div className="d-flex justify-content-between mt-3 fees-content-1">
                    <div>
                      <div>
                        {/* show only payable family members (exclude children) */}
                        Family Member(s){" "}
                        {
                          (pendingMember ? tempFamilyMembers : members).filter(
                            (m) => {
                              const isChild =
                                m.is_child ?? calculateIsChild(m.date_of_birth);
                              return !isChild;
                            }
                          ).length
                        }{" "}
                        × ${plan?.amount}
                      </div>
                      {plan?.discount_amount > 0 && discountValid && (
                        <div className="small">
                          ($
                          {discountAmount *
                            (pendingMember
                              ? tempFamilyMembers
                              : members
                            ).filter((m) => {
                              const isChild =
                                m.is_child ?? calculateIsChild(m.date_of_birth);
                              return !isChild;
                            }).length}{" "}
                          discount applied)
                        </div>
                      )}
                    </div>

                    <strong>${familyAmount}</strong>
                  </div>
                )}

                <hr />

                {/* Total Discount */}
                {plan.discount_amount > 0 && discountValid && (
                  <>
                    <div className="d-flex justify-content-between">
                      <strong>Total Discount</strong>
                      <strong>-${totalDiscount}</strong>
                    </div>
                    <hr />
                  </>
                )}

                {/* Total Amount */}
                <div className="d-flex justify-content-between total-amount my-2">
                  <p className="fw-bold mb-0">Total Amount</p>
                  <p className="fw-bold mb-0">${totalAmount}</p>
                </div>

                <Button
                  className="btn-orange-lg w-100 my-3"
                  onClick={handleProcessToPay}
                >
                  Process to Pay
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <MemberModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editData={editData}
        refreshList={fetchMembers}
        onSaveLocal={pendingMember ? handleSaveLocal : null}
        existingItems={pendingMember ? tempFamilyMembers : []}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          handleDeleteConfirmed(selectedRow);
          setShowDeleteModal(false);
        }}
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.first_name}</strong>?
          </>
        }
      />
    </div>
  );
}

export default RegisterMembership;
